import React, { Component } from "react";
import { observer } from "mobx-react";
import { action, observable } from "mobx";
import {
  Button,
  Checkbox,
  Dimmer,
  Form,
  Grid,
  Header,
  Input,
  Label,
  Loader,
  Message,
  Modal,
  TextArea,
} from "semantic-ui-react";
import PropTypes from "prop-types";
import PromiseButton from "~/components/element/PromiseButton";
import ProductId from "~/constants/ProductId";
import TourJob from "~/models/TourJob/TourJob";
import TourJobType from "~/constants/TourJob/TourJobType";
import TourJobTypeLabel from "~/constants/TourJob/TourJobTypeLabel";
import Immersive3dTour from "~/models/Immersive3dTour/Immersive3dTour";
import FlashMessageStore from "~/stores/FlashMessageStore";
import FlashMessage from "~/components/FlashMessage";
import ContentLoader from "~/components/element/ContentLoader";
import Immersive3dTourPropertyTypeLabel from "~/constants/Immersive3dTour/Immersive3dTourPropertyTypeLabel";
import CardNotEnoughCredits from "./ProcessingModal/CardNotEnoughCredits";
import PriceTableForPricingMethodBasePriceAndPerPanorama from "./ProcessingModal/PriceTableForPricingMethodBasePriceAndPerPanorama";
import Immersive3dTourPricingMethod from "~/constants/Immersive3dTour/Immersive3dTourPricingMethod";
import PriceTableForPricingMethodFlatFee from "./ProcessingModal/PriceTableForPricingMethodFlatFee";
import PriceTableForPricingMethodPerSquareFeet from "./ProcessingModal/PriceTableForPricingMethodPerSquareFeet";
import FormStore from "~/stores/FormStore";
import Immersive3dTourTabStore from "~/stores/Immersive3dTour/Immersive3dTourTabStore";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import CreditUsageStore from "~/stores/CreditUsageStore";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import "../styles/ProcessingModal.less";

@observer
class ProcessingModal extends Component {
  flashMessageStore = null;

  /**
   * @property {CreditUsageStore}
   */
  creditUsageStore = null;

  @observable formStore = null;

  constructor(props) {
    super(props);

    this.bindForm(props.immersive3dTour);

    this.state = {
      visible: false,
      newTourJobTypeId: TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN,
      isLoading: false,
      notEnoughCredits: false,
      isSendingForProcessing: false,
    };

    this.flashMessageStore = new FlashMessageStore(10000);

    this.creditUsageStore = new CreditUsageStore(null);
    // We need the maximum possible rows
    // We hope we won't have more than 100 rows for credit usages for one product and one tour at a time
    this.creditUsageStore.pagination.itemsPerPage = 100;
    this.creditUsageStore.isLoading = true;
  }

  componentDidMount() {
    const { immersive3dTour, showError, panoramasCount } = this.props;

    // Only for new Immersive 3D Tours we need to check if the Publish Tour product is paid
    if (panoramasCount === 0) {
      this.creditUsageStore.updateSearchParams({
        tourId: immersive3dTour.tourId,
        productId: ProductId.PUBLISH_TOUR,
      });

      this.creditUsageStore.loadIndex().catch(error => showError(error.message));
    }
  }

  getTourJobTypeId() {
    const { tourJob } = this.props;
    const { newTourJobTypeId } = this.state;
    return tourJob ? tourJob.typeId : newTourJobTypeId;
  }

  getPanoramaProduct() {
    return this.getTourJobTypeId() === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
      ? ProductId.IMMERSIVE_3D_TOUR_PANORAMAS
      : ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN;
  }

  show = () => this.setState({ visible: true });

  hide = () => this.setState({ visible: false });

  handleNewTourJobTypeIdChange = (e, { value }) => this.setState({ newTourJobTypeId: value });

  bindForm(immersive3dTour) {
    const initialModel = {
      notes: immersive3dTour.notes || "",
    };

    if (immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.PER_SQUARE_FEET) {
      const estimatedShootSize = immersive3dTour.estimatedShootSize
        ? immersive3dTour.estimatedShootSize.toString()
        : "";

      initialModel.estimatedShootSize = immersive3dTour.floorPlanSize
        ? String(Math.ceil(immersive3dTour.floorPlanSize))
        : estimatedShootSize;
    }

    this.formStore = new FormStore(initialModel);
  }

  @action
  process() {
    const { immersive3dTour, immersive3dTourTabStore } = this.props;
    const { model } = this.formStore;

    this.setState({ isLoading: true, isSendingForProcessing: true });

    let estimatedShootSize = null;

    if (
      immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.PER_SQUARE_FEET &&
      parseInt(model.estimatedShootSize, 10) !== immersive3dTour.estimatedShootSize
    ) {
      estimatedShootSize = parseInt(model.estimatedShootSize, 10);
    }

    immersive3dTourTabStore
      .process(this.getTourJobTypeId(), model.notes, estimatedShootSize)
      .then(() => {
        this.hide();
      })
      .catch(error => {
        if (typeof error.hasValidationErrors === "undefined") {
          this.flashMessageStore.setMessage(error.message, "negative");

          if (error.message === "Not enough credits to proceed!") {
            this.setState({ notEnoughCredits: true });
          }
        }
      })
      .finally(() => {
        this.setState({ isLoading: false, isSendingForProcessing: false });
      });
  }

  render() {
    const { visible, newTourJobTypeId, isLoading, notEnoughCredits, isSendingForProcessing } = this.state;
    const {
      hasWarnings,
      hasErrors,
      panoramasCount,
      newPanoramasCount,
      orphanPanoramasCount,
      unusedFloorsCount,
      immersive3dTour,
      tourJob,
      immersive3dTourTabStore,
      productDiscountedCreditCosts,
    } = this.props;
    const { model, setProperty, getAggregatedError } = this.formStore;
    const { errorMessages } = immersive3dTourTabStore;

    if (immersive3dTour.propertyType === null) {
      return null;
    }

    const { isLoading: creditUsageLoading, creditUsageSummary } = this.creditUsageStore;

    let warningMessage;

    if (orphanPanoramasCount > 0) {
      warningMessage = (
        <span>
          There is one or more panoramas in the main bucket that must be moved to a proper floor section before
          proceeding.
        </span>
      );
    } else if (unusedFloorsCount > 0) {
      warningMessage = (
        <span>There is one or more floors without panoramas that must be used or removed before proceeding.</span>
      );
    } else if (hasErrors) {
      warningMessage = (
        <span>
          One or multiple errors will prevent this Immersive 3D Tour from being successfully processed. Please go back
          to fix these errors.
        </span>
      );
    }

    if (warningMessage) {
      return (
        <>
          <Button primary onClick={() => this.show()} disabled={hasErrors}>
            Process
          </Button>
          <Modal open={visible} onClose={() => this.hide()} size="small">
            <Modal.Header>Warning</Modal.Header>
            <Modal.Content>
              <Modal.Description>{warningMessage}</Modal.Description>
            </Modal.Content>
            <Modal.Actions>
              <Button primary onClick={() => this.hide()}>
                Ok
              </Button>
            </Modal.Actions>
          </Modal>
        </>
      );
    }

    let validationSchema = {};

    if (immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.PER_SQUARE_FEET) {
      validationSchema = {
        estimatedShootSize: {
          required: true,
          isInt: true,
        },
      };
    }
    const validation = getValidationErrors(validationSchema, model);

    let modalContentDescription = <ContentLoader />;

    if (isLoading) {
      if (isSendingForProcessing) {
        modalContentDescription = (
          <Dimmer active inverted>
            <Loader
              inverted
              content={
                <div style={{ width: "400px" }}>
                  <p>We are creating your Immersive 3D Tour, please wait...</p>
                </div>
              }
            />
          </Dimmer>
        );
      }
    } else {
      let priceTable = null;

      if (immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.BASE_PRICE_AND_PER_PANORAMA) {
        priceTable = (
          <PriceTableForPricingMethodBasePriceAndPerPanorama
            tourJobTypeId={this.getTourJobTypeId()}
            panoramasCount={panoramasCount}
            newPanoramasCount={newPanoramasCount}
            panoramaProduct={this.getPanoramaProduct()}
            productDiscountedCreditCosts={productDiscountedCreditCosts}
          />
        );
      } else if (immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.FLAT_FEE) {
        priceTable = (
          <PriceTableForPricingMethodFlatFee
            tourJobTypeId={this.getTourJobTypeId()}
            panoramasCount={panoramasCount}
            newPanoramasCount={newPanoramasCount}
            productDiscountedCreditCosts={productDiscountedCreditCosts}
          />
        );
      } else if (immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.PER_SQUARE_FEET) {
        priceTable = (
          <PriceTableForPricingMethodPerSquareFeet
            tourJobTypeId={this.getTourJobTypeId()}
            panoramasCount={panoramasCount}
            newPanoramasCount={newPanoramasCount}
            floorPlanSize={immersive3dTour.floorPlanSize}
            estimatedShootSize={immersive3dTour.estimatedShootSize}
            newEstimatedShootSize={model.estimatedShootSize ? parseInt(model.estimatedShootSize, 10) : null}
            productDiscountedCreditCosts={productDiscountedCreditCosts}
            creditUsageLoading={creditUsageLoading}
            creditUsageSummary={creditUsageSummary}
          />
        );
      }

      let selectPropertyDescription = null;
      let tourJobTypeFormFields = null;

      if (tourJob) {
        if (immersive3dTour.pricingMethod !== Immersive3dTourPricingMethod.PER_SQUARE_FEET) {
          selectPropertyDescription = (
            <p>
              You have selected <strong>{Immersive3dTourPropertyTypeLabel[immersive3dTour.propertyType]}</strong>,{" "}
              <strong>{TourJobTypeLabel[tourJob.typeId]}</strong>.
            </p>
          );
        } else {
          selectPropertyDescription = (
            <p>
              You have selected <strong>{TourJobTypeLabel[tourJob.typeId]}</strong>.
            </p>
          );
        }
      } else {
        if (immersive3dTour.pricingMethod !== Immersive3dTourPricingMethod.PER_SQUARE_FEET) {
          selectPropertyDescription = (
            <p>
              You have selected property type:{" "}
              <strong>{Immersive3dTourPropertyTypeLabel[immersive3dTour.propertyType]}</strong>.
            </p>
          );
        }

        tourJobTypeFormFields = (
          <>
            <Form.Field>
              <label>Please select:</label>
              <Checkbox
                label="With an enhanced floor plan"
                name="radioGroup"
                value={TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN}
                checked={newTourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN}
                onChange={this.handleNewTourJobTypeIdChange}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label="With a basic floor plan"
                name="radioGroup"
                value={TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS}
                checked={newTourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS}
                onChange={this.handleNewTourJobTypeIdChange}
              />
            </Form.Field>
          </>
        );
      }

      modalContentDescription = (
        <>
          <FlashMessage flashMessageStore={this.flashMessageStore} />
          <Grid divided>
            <Grid.Row>
              <Grid.Column width={6}>
                {hasWarnings && (
                  <Message warning>
                    <Message.Header>We found one or multiple warnings with your panoramas</Message.Header>
                    <p>
                      While we can probably process your Immersive 3D Tour correctly, warnings can cause unexpected
                      results. For optimal results, you should try fixing them in the last step before confirming your
                      order.
                    </p>
                  </Message>
                )}

                {immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.PER_SQUARE_FEET &&
                  !model.estimatedShootSize && (
                    <Message warning>
                      <Message.Header>No estimated shoot size</Message.Header>
                      <p>
                        We will need an estimate of the shoot size in order to provide an estimate of the credit cost.
                      </p>
                    </Message>
                  )}

                {notEnoughCredits && <CardNotEnoughCredits />}

                {selectPropertyDescription}
                <Form>
                  {tourJobTypeFormFields}
                  {immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.PER_SQUARE_FEET && (
                    <Form.Field>
                      <label htmlFor="shoot-size">* Estimated shoot size</label>
                      <Input
                        fluid
                        label={{ basic: true, content: "sq. ft." }}
                        labelPosition="right"
                        id="shoot-size"
                        name="estimatedShootSize"
                        placeholder="2000"
                        value={model.estimatedShootSize}
                        onChange={e => setProperty("estimatedShootSize", e.target.value.replace(/\D/g, ""))}
                        error={getAggregatedError("estimatedShootSize", validation, errorMessages)}
                      />
                      {typeof getAggregatedError("estimatedShootSize", validation, errorMessages) === "object" && (
                        <Label pointing prompt>
                          {getAggregatedError("estimatedShootSize", validation, errorMessages).content}
                        </Label>
                      )}
                    </Form.Field>
                  )}
                  <Form.Field>
                    <label>Notes</label>
                    <TextArea
                      name="notes"
                      placeholder="Tell us more"
                      value={model.notes}
                      onChange={e => setProperty("notes", e.target.value)}
                    />
                  </Form.Field>
                </Form>

                <p style={{ marginTop: "1em" }}>
                  NOTE: Once you start the processing, you won&apos;t be able to make changes to the panoramas. You can
                  add additional panoramas, though, when the processing is complete.
                </p>
              </Grid.Column>
              <Grid.Column width={10}>{priceTable}</Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      );
    }

    return (
      <>
        <Button primary onClick={this.show} disabled={hasErrors}>
          Process
        </Button>
        <Modal open={visible} className="processingModal notranslate">
          <Header content="Process this Immersive 3D Tour?" />
          <Modal.Content>
            <Modal.Description>{modalContentDescription}</Modal.Description>
          </Modal.Content>
          {!isLoading && (
            <Modal.Actions>
              <Button secondary onClick={this.hide}>
                Close
              </Button>

              <PromiseButton
                color="green"
                onClick={() => {
                  this.process();
                }}
                disabled={!validation.isValid}
              >
                {immersive3dTour.pricingMethod === Immersive3dTourPricingMethod.PER_SQUARE_FEET
                  ? "Proceed"
                  : "Pay & Proceed"}
              </PromiseButton>
            </Modal.Actions>
          )}
        </Modal>
      </>
    );
  }
}

ProcessingModal.propTypes = {
  showError: PropTypes.func.isRequired,
  hasWarnings: PropTypes.bool.isRequired,
  hasErrors: PropTypes.bool.isRequired,
  panoramasCount: PropTypes.number.isRequired,
  newPanoramasCount: PropTypes.number.isRequired,
  orphanPanoramasCount: PropTypes.number.isRequired,
  unusedFloorsCount: PropTypes.number.isRequired,
  immersive3dTour: PropTypes.instanceOf(Immersive3dTour).isRequired,
  tourJob: PropTypes.instanceOf(TourJob),
  immersive3dTourTabStore: PropTypes.instanceOf(Immersive3dTourTabStore),
  productDiscountedCreditCosts: PropTypes.shape({
    [ProductId.PUBLISH_TOUR]: PropTypes.number.isRequired,
    // Pricing method with base price and per panorama
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_BASE]: PropTypes.number.isRequired,
    // Pricing method with flat fee
    [ProductId.IMMERSIVE_3D_TOUR]: PropTypes.number.isRequired,
    [ProductId.FLOOR_PLAN]: PropTypes.number.isRequired,
    // Pricing method per square feet
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_0_2500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_0_2500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_2501_3500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_2501_3500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_3501_5000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_3501_5000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_5001_7500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_5001_7500_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_7501_10000_SQ_FT]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_WITH_FLOOR_PLAN_7501_10000_SQ_FT]: PropTypes.number.isRequired,
  }).isRequired,
};

export default ErrorWrapper(ProcessingModal);
